import { Sine, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import { OnboardingStep } from "../Data/OnboardingStep";
import * as SoundEffect from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import { IOnboardingTarget } from "../GameObjects/IOnboardingTarget";

export class OnboardingManager extends PIXI.Container {
    private _steps: OnboardingStep[];
    private _activeStep: OnboardingStep | undefined;
    private _activeStepIndex: number;
    private _arrowContainer: PIXI.Container;
    private _arrow: PIXI.Sprite;
    private _arrowTimer: TimelineLite | undefined;
    private _arrowTween: TweenMax | undefined;
    private _messageSubscription: any;
    private _forceSubscription: any;

    constructor() {
        super();

        this._steps = new Array<OnboardingStep>();
        this._activeStepIndex = -1;

        this._arrowContainer = new PIXI.Container();
        this.addChild(this._arrowContainer);

        this._arrow = HLF.PixiUtils.makeSprite("onboarding-arrow", Constants.SPRITESHEET_UI);
        this._arrow.anchor.set(0.5, 1);
        this._arrow.alpha = 0;
        this._arrow.visible = false;
        this._arrowContainer.addChild(this._arrow);
    }

    public clear(): void {
        this._steps.length = 0;
    }

    public addStep(pEvent: string, pData: any, pTarget: IOnboardingTarget | undefined, pDelay: number,
                   pIsOrdered: boolean, pFailure?: string, pOnStart?: () => void, pOnComplete?: () => void): void {
        if (Constants.DEBUG_FAST_ONBOARDING) {
            pDelay = 0.75;
        }
        this._steps.push(new OnboardingStep(
            pEvent, pData, pTarget, pDelay, pIsOrdered, pFailure, pOnStart, pOnComplete,
        ));
    }

    public start() {
        this._activeStepIndex = 0;
        this.beginStep();
        if (this._messageSubscription !== undefined) {
            PubSub.unsubscribe(this._messageSubscription);
        }
        if (this._forceSubscription !== undefined) {
            PubSub.unsubscribe(this._forceSubscription);
        }
        this._messageSubscription = PubSub.subscribe(Topics.ANDY, this.onMessageReceived.bind(this));
        this._forceSubscription = PubSub.subscribe(Topics.FORCE_ONBOARDING_HINT, this.forceArrow.bind(this));
        PubSub.publishSync(Topics.ONBOARDING_START, undefined);
    }

    public onResize(): void {
        if (this._activeStep !== undefined && this._arrowTween !== undefined && this._arrowTween.isActive) {
            this.hideArrow(true);
            if (this._arrowTimer !== undefined) {
                this._arrowTimer.kill();
            }
            this._arrowTimer = new TimelineLite();
            this._arrowTimer.call(this.showArrow.bind(this), undefined, this, 0.25);
        }
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        PubSub.unsubscribe(this._messageSubscription);
        PubSub.unsubscribe(this._forceSubscription);
        super.destroy(pOptions);
    }

    private onOnboardingComplete(): void {
        PubSub.unsubscribe(this._messageSubscription);
        PubSub.unsubscribe(this._forceSubscription);
        this._messageSubscription = undefined;
        this._forceSubscription = undefined;
        this._activeStep = undefined;
        this._activeStepIndex = -1;
        PubSub.publishSync(Topics.ONBOARDING_FINISH, undefined);
    }

    private showArrow(): void {
        if (this._activeStep!.target === undefined) {
            return;
        }
        if (this._arrowTween !== undefined) {
            this._arrowTween.kill();
        }
        const globalPos: PIXI.Point = this._activeStep!.target.getOnboardingPosition();
        const localPos: PIXI.Point = this.transform.worldTransform.applyInverse(globalPos);
        this._arrow.visible = true;
        this._arrowTimer = undefined;
        this._arrowContainer.position.copy(localPos);
        this._arrow.position.y = -50;
        this._arrowContainer.rotation = this._activeStep!.target.getOnboardingRotation();
        TweenMax.to(this._arrow, 0.25, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
        this._arrowTween = new TweenMax(this._arrow, 0.5, {
            y: 0,
            ease: Sine.easeInOut,
            repeat: -1,
            yoyo: true,
        });
        if (Constants.DEBUG_MINIMAL_AUDIO === false) {
            PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                SoundEffect.ONBOARDING_SHOW, 1, false, HLF.AudioCategory.SFX,
            ));
        }
        PubSub.publishSync(Topics.ONBOARDING_ARROW_SHOW, undefined);
    }

    private hideArrow(pInstantly: boolean = false): void {
        if (this._arrowTween !== undefined) {
            this._arrowTween.kill();
        }
        TweenMax.to(this._arrow, pInstantly ? 0 : 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
            onComplete: () => { this._arrow.visible = false; },
        });
    }

    private forceArrow(): void {
        if (this._arrowTimer !== undefined) {
            this._arrowTimer.kill();
            this.showArrow();
        }
    }

    private beginStep(pForceArrow: boolean = false): void {
        if (this._arrowTween !== undefined) {
            this._arrowTween.kill();
            this.hideArrow();
        }
        if (this._arrowTimer !== undefined) {
            this._arrowTimer.kill();
        }
        // Find the next uncompleted onboarding step
        while (this._activeStepIndex < this._steps.length && this._steps[this._activeStepIndex].isComplete) {
            ++this._activeStepIndex;
        }
        // If we are not yet done onboarding
        if (this._activeStepIndex < this._steps.length) {
            this._activeStep = this._steps[this._activeStepIndex];
            this._arrowTimer = new TimelineLite();
            this._arrowTimer.call(() => {
                if (this._activeStep!.onStart !== undefined) {
                    this._activeStep!.onStart();
                }
                this.showArrow();
            }, undefined, this, pForceArrow ? 0.75 : this._activeStep.hintDelay);
        }
        // We have finished all onboarding steps
        else {
            this.onOnboardingComplete();
        }
    }

    private onMessageReceived(pTopic: string, pData?: any): void {
        if (this._activeStep === undefined) {
            return;
        }
        // If we just performed the current onboarding step
        const correctAction: boolean = this._activeStep.event === pTopic;
        const correctData: boolean = this._activeStep.data === undefined || this._activeStep!.data === pData;
        const failAction: boolean = this._activeStep.failure === pTopic;
        if (correctAction && correctData) {
            this._activeStep.isComplete = true;
            if (this._activeStep.onComplete !== undefined) {
                this._activeStep.onComplete();
            }
            ++this._activeStepIndex;
            this.hideArrow();
            this.beginStep();
        }
        else if (failAction && correctData) {
            --this._activeStepIndex;
            this._steps[this._activeStepIndex].isComplete = false;
            this.hideArrow();
            this.beginStep(true);
        }
        // We didn't perform the current step, check to see if this was a later step
        else {
            for (let i = this._activeStepIndex + 1; i < this._steps.length; ++i) {
                if (
                    this._steps[i].isOrdered === false &&
                    this._steps[i].event === pTopic &&
                    this._steps[i].data === pData
                ) {
                    this._steps[i].isComplete = true;
                    break;
                }
            }
        }
    }
}
